/** Routes, and the gate in front of each screen: session, password, MFA, role. */
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Alert, Skeleton } from '../components/ui';
import { AppShell } from './AppShell';
import { IntegrationDashboardPage } from './integrations/IntegrationDashboardPage';
import { ApiClientsPage } from './integrations/ApiClientsPage';
import { WebhooksPage } from './integrations/WebhooksPage';
import { EventTypesPage } from './integrations/EventTypesPage';
import { PostingRuleEditorPage, PostingRulesPage } from './integrations/PostingRulesPage';
import { MappingTablesPage } from './integrations/MappingTablesPage';
import { EventsPage } from './integrations/EventsPage';
import { OAuthConsentPage } from './integrations/OAuthConsentPage';
import { AdvisorPortfolioPage, AdvisorTeamPage } from './advisor/AdvisorPages';
import { AdvisorFirmsPage } from './setup/AdvisorFirmsPage';
import { FirmsPage } from './platform/FirmsPage';
import { DataPage, ImportPage, SupportAccessPage } from './setup/DataPages';
import { RecurringPage } from './accounting/RecurringPage';
import { AccountPage } from './AccountPage';
import { HelpPage } from './help/HelpPage';
import { AdjustmentsPage, LocationsPage, MovesPage, StockPage } from './inventory/InventoryPages';
import { LoginPage } from './LoginPage';
import { ChangePasswordPage } from './ChangePasswordPage';
import { MfaSetupPage } from './MfaSetupPage';
import { DashboardPage } from './DashboardPage';
import { JournalsPage } from './accounting/JournalsPage';
import { JournalEditorPage } from './accounting/JournalEditorPage';
import { JournalDetailPage } from './accounting/JournalDetailPage';
import { AccountsPage } from './accounting/AccountsPage';
import { DimensionsPage } from './accounting/DimensionsPage';
import { PeriodsPage } from './accounting/PeriodsPage';
import { TrialBalancePage } from './reports/TrialBalancePage';
import { GeneralLedgerPage } from './reports/GeneralLedgerPage';
import { CompanyPage } from './setup/CompanyPage';
import { BranchesPage } from './setup/BranchesPage';
import { UsersPage } from './setup/UsersPage';
import { RolesPage } from './setup/RolesPage';
import { AuditLogPage } from './setup/AuditLogPage';
import { CompaniesPage } from './platform/CompaniesPage';
import { ContactsPage } from './sales/ContactsPage';
import { ItemsPage } from './sales/ItemsPage';
import { SalesListPage } from './sales/SalesListPage';
import { SalesEditorPage } from './sales/SalesEditorPage';
import { SalesDetailPage } from './sales/SalesDetailPage';
import { ReceiptDetailPage, ReceiptEditorPage, ReceiptsPage } from './sales/ReceiptsPages';
import { Missing2307Page } from './sales/Missing2307Page';
import { AgedReceivablesPage } from './reports/AgedReceivablesPage';
import { CustomerStatementPage } from './reports/CustomerStatementPage';
import { PurchaseListPage } from './purchases/PurchaseListPage';
import { PurchaseEditorPage } from './purchases/PurchaseEditorPage';
import { PurchaseDetailPage } from './purchases/PurchaseDetailPage';
import { PaymentDetailPage, PaymentEditorPage, PaymentsPage } from './purchases/PaymentsPages';
import { Wht2307Page } from './purchases/Wht2307Page';
import { AgedPayablesPage, CashRequirementsPage, SupplierStatementPage } from './reports/PayablesReports';
import { BankAccountsPage } from './banking/BankAccountsPage';
import { ReconcilePage } from './banking/ReconcilePage';
import { BankRulesPage, PdcPage, PettyCashPage } from './banking/BankingExtrasPages';
import { TaxCenterPage } from './tax/TaxCenterPage';
import { ReturnDetailPage } from './tax/ReturnDetailPage';
import { TaxChecksPage } from './tax/TaxChecksPage';
import { BooksPage } from './reports/BooksPage';
import { StatementsPage } from './reports/StatementsPage';
import { AnalysisPage } from './reports/AnalysisPage';
import { ArchivePage } from './reports/ArchivePage';

function Protected({ children, permissions, platform, firm }: { children: ReactNode; permissions?: string[]; platform?: boolean; firm?: boolean }): JSX.Element {
  const { user, loading, can } = useAuth();
  if (loading) {
    return (
      <div className="p-8">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!user) {
    try {
      if (window.location.pathname !== '/') sessionStorage.setItem('cx_return', window.location.pathname + window.location.search);
    } catch {
      /* storage unavailable */
    }
    return <Navigate to="/login" replace />;
  }
  if (user.mustResetPassword) return <Navigate to="/change-password" replace />;
  if (!user.mfaEnabled) return <Navigate to="/mfa-setup" replace />;
  const allowed = platform ? user.isPlatformAdmin : firm ? Boolean(user.firm) : !permissions || can(...permissions);
  if (!allowed) {
    return (
      <AppShell>
        <Alert tone="critical">Your role does not give you access to this page. Ask your administrator if you need it.</Alert>
      </AppShell>
    );
  }
  return <AppShell>{children}</AppShell>;
}

/** Help is readable without signing in, so outside developers can use the API guide. */
function PublicHelp(): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8"><Skeleton className="h-64 w-full" /></div>;
  if (user && user.mfaEnabled && !user.mustResetPassword) return <AppShell><HelpPage /></AppShell>;
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <a href="/help" className="text-lg font-bold text-navy dark:text-white">CoreX-lite <span className="font-medium text-slate-500">Help & API guide</span></a>
          <a href="/login" className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-700">Sign in</a>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6"><HelpPage /></main>
    </div>
  );
}

const page = (element: JSX.Element, permissions?: string[]): JSX.Element => <Protected permissions={permissions}>{element}</Protected>;

export function App(): JSX.Element {
  const { user, loading } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={loading ? <div /> : user && user.mfaEnabled && !user.mustResetPassword ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/change-password" element={user ? <ChangePasswordPage /> : <Navigate to="/login" replace />} />
      <Route path="/mfa-setup" element={loading ? <div /> : user ? (user.mustResetPassword ? <Navigate to="/change-password" replace /> : <MfaSetupPage />) : <Navigate to="/login" replace />} />

      <Route path="/" element={page(<DashboardPage />)} />

      <Route path="/sales/invoices" element={page(<SalesListPage list="invoices" />, ['sales.view'])} />
      <Route path="/sales/quotations" element={page(<SalesListPage list="quotations" />, ['sales.view'])} />
      <Route path="/sales/memos" element={page(<SalesListPage list="memos" />, ['sales.view'])} />
      <Route path="/sales/new/:kind" element={page(<SalesEditorPage />, ['sales.create', 'sales.manage'])} />
      <Route path="/sales/:id" element={page(<SalesDetailPage />, ['sales.view'])} />
      <Route path="/sales/:id/edit" element={page(<SalesEditorPage />, ['sales.create', 'sales.manage'])} />
      <Route path="/receipts" element={page(<ReceiptsPage />, ['receipts.view'])} />
      <Route path="/receipts/new" element={page(<ReceiptEditorPage />, ['receipts.create', 'receipts.manage'])} />
      <Route path="/receipts/:id" element={page(<ReceiptDetailPage />, ['receipts.view'])} />
      <Route path="/receipts/:id/edit" element={page(<ReceiptEditorPage />, ['receipts.create', 'receipts.manage'])} />
      <Route path="/receipts/2307-tracker" element={page(<Missing2307Page />, ['receipts.view'])} />
      <Route path="/customers" element={page(<ContactsPage role="customer" />, ['customers.view'])} />
      <Route path="/items" element={page(<ItemsPage />, ['sales.view', 'bills.view', 'coa.view'])} />
      <Route path="/reports/aged-receivables" element={page(<AgedReceivablesPage />, ['reports.ar', 'reports.view'])} />
      <Route path="/reports/customer-statement" element={page(<CustomerStatementPage />, ['reports.ar', 'reports.view', 'customers.view'])} />

      <Route path="/purchases/bills" element={page(<PurchaseListPage list="bills" />, ['bills.view'])} />
      <Route path="/purchases/orders" element={page(<PurchaseListPage list="orders" />, ['bills.view'])} />
      <Route path="/purchases/new/:kind" element={page(<PurchaseEditorPage />, ['bills.manage'])} />
      <Route path="/purchases/:id" element={page(<PurchaseDetailPage />, ['bills.view'])} />
      <Route path="/purchases/:id/edit" element={page(<PurchaseEditorPage />, ['bills.manage'])} />
      <Route path="/payments" element={page(<PaymentsPage />, ['payments.view'])} />
      <Route path="/payments/new" element={page(<PaymentEditorPage />, ['payments.create', 'pettycash.manage'])} />
      <Route path="/payments/:id" element={page(<PaymentDetailPage />, ['payments.view'])} />
      <Route path="/payments/:id/edit" element={page(<PaymentEditorPage />, ['payments.create', 'pettycash.manage'])} />
      <Route path="/suppliers" element={page(<ContactsPage role="supplier" />, ['suppliers.view'])} />
      <Route path="/tax/2307-issued" element={page(<Wht2307Page />, ['tax.view', 'wht.issue'])} />
      <Route path="/reports/aged-payables" element={page(<AgedPayablesPage />, ['reports.ap', 'reports.view'])} />
      <Route path="/reports/cash-requirements" element={page(<CashRequirementsPage />, ['reports.ap', 'reports.view'])} />
      <Route path="/reports/supplier-statement" element={page(<SupplierStatementPage />, ['reports.ap', 'reports.view', 'suppliers.view'])} />

      <Route path="/banking" element={page(<BankAccountsPage />, ['bank.view'])} />
      <Route path="/banking/rules" element={page(<BankRulesPage />, ['bank.view'])} />
      <Route path="/banking/pdc" element={page(<PdcPage />, ['pdc.view'])} />
      <Route path="/banking/petty-cash" element={page(<PettyCashPage />, ['bank.view', 'pettycash.manage'])} />
      <Route path="/banking/:accountId" element={page(<ReconcilePage />, ['bank.view'])} />

      <Route path="/tax" element={page(<TaxCenterPage />, ['tax.view'])} />
      <Route path="/tax/returns/:id" element={page(<ReturnDetailPage />, ['tax.view'])} />
      <Route path="/tax/checks" element={page(<TaxChecksPage />, ['tax.view'])} />

      <Route path="/reports/books/:book" element={page(<BooksPage />, ['reports.view'])} />
      <Route path="/reports/statements/:kind" element={page(<StatementsPage />, ['reports.view'])} />
      <Route path="/reports/analysis" element={page(<AnalysisPage />, ['reports.view', 'reports.ar', 'reports.ap'])} />
      <Route path="/reports/archive" element={page(<ArchivePage />, ['reports.view', 'audit.view'])} />

      <Route path="/journals" element={page(<JournalsPage />, ['journals.view'])} />
      <Route path="/journals/new" element={page(<JournalEditorPage />, ['journals.create'])} />
      <Route path="/journals/:id" element={page(<JournalDetailPage />, ['journals.view'])} />
      <Route path="/journals/:id/edit" element={page(<JournalEditorPage />, ['journals.create'])} />
      <Route path="/opening-balances" element={page(<JournalEditorPage mode="opening" />, ['journals.create'])} />
      <Route path="/accounts" element={page(<AccountsPage />, ['coa.view'])} />
      <Route path="/dimensions" element={page(<DimensionsPage />, ['coa.view'])} />
      <Route path="/periods" element={page(<PeriodsPage />, ['periods.manage', 'journals.view'])} />

      <Route path="/reports/trial-balance" element={page(<TrialBalancePage />, ['reports.view'])} />
      <Route path="/reports/general-ledger" element={page(<GeneralLedgerPage />, ['reports.view'])} />

      <Route path="/setup/company" element={page(<CompanyPage />, ['company.view', 'company.manage'])} />
      <Route path="/setup/branches" element={page(<BranchesPage />, ['company.view', 'company.manage'])} />
      <Route path="/setup/users" element={page(<UsersPage />, ['users.view', 'users.manage'])} />
      <Route path="/setup/roles" element={page(<RolesPage />, ['users.view', 'users.manage'])} />
      <Route path="/setup/audit" element={page(<AuditLogPage />, ['audit.view'])} />

      <Route path="/integrations" element={page(<IntegrationDashboardPage />, ['integrations.manage', 'rules.view', 'audit.view', 'users.view'])} />
      <Route path="/integrations/clients" element={page(<ApiClientsPage />, ['integrations.manage', 'audit.view'])} />
      <Route path="/integrations/webhooks" element={page(<WebhooksPage />, ['integrations.manage', 'audit.view', 'rules.view'])} />
      <Route path="/integrations/event-types" element={page(<EventTypesPage />, ['rules.view', 'events.define'])} />
      <Route path="/integrations/rules" element={page(<PostingRulesPage />, ['rules.view'])} />
      <Route path="/integrations/rules/:ruleId" element={page(<PostingRuleEditorPage />, ['rules.view'])} />
      <Route path="/integrations/mappings" element={page(<MappingTablesPage />, ['rules.view'])} />
      <Route path="/integrations/events" element={page(<EventsPage />, ['rules.view', 'exceptions.manage'])} />
      <Route path="/oauth/authorize" element={page(<OAuthConsentPage />)} />

      <Route path="/advisor" element={<Protected firm><AdvisorPortfolioPage /></Protected>} />
      <Route path="/advisor/team" element={<Protected firm><AdvisorTeamPage /></Protected>} />
      <Route path="/setup/advisors" element={page(<AdvisorFirmsPage />, ['users.view', 'users.manage'])} />
      <Route path="/setup/support" element={page(<SupportAccessPage />, ['users.view', 'users.manage'])} />
      <Route path="/setup/data" element={page(<DataPage />, ['users.manage', 'company.view'])} />
      <Route path="/setup/import" element={page(<ImportPage />, ['coa.manage', 'customers.manage', 'suppliers.manage', 'sales.manage', 'bills.manage', 'journals.create'])} />
      <Route path="/inventory" element={page(<StockPage />, ['inventory.view'])} />
      <Route path="/inventory/moves" element={page(<MovesPage />, ['inventory.view'])} />
      <Route path="/inventory/adjustments" element={page(<AdjustmentsPage />, ['inventory.view'])} />
      <Route path="/inventory/locations" element={page(<LocationsPage />, ['inventory.view'])} />
      <Route path="/help" element={<PublicHelp />} />
      <Route path="/help/:id" element={<PublicHelp />} />
      <Route path="/account" element={page(<AccountPage />)} />
      <Route path="/recurring" element={page(<RecurringPage />, ['journals.view', 'sales.view'])} />
      <Route path="/platform/firms" element={<Protected platform><FirmsPage /></Protected>} />
      <Route path="/platform/companies" element={<Protected platform><CompaniesPage /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
